import React from 'react';
import { useTranslation } from 'react-i18next';
import { TouchableOpacity } from 'react-native';

import Icon from '@components/shared/Icon/Icon';
import { Spinner } from '@components/shared/Spinner';

import theme from '../../../themes/theme';
import { Box, Text } from '../Restyle';

interface ModalHeaderProps {
  title: string;
  submitLabel?: string;
  onCancel: () => void;
  onSubmit?: () => void;
  onSearch?: () => void;
  showCancel?: boolean;
  cancelColor?: keyof typeof theme.colors;
  submitColor?: keyof typeof theme.colors;
  disabled?: boolean;
  showSearch?: boolean;
  /** TA-10279: additive, defaults false so every other caller is unaffected.
   * Locks both Cancel and Submit and swaps the Submit label for a spinner
   * while a caller's mutation is in flight. */
  isLoading?: boolean;
}

export const ModalHeader: React.FC<ModalHeaderProps> = ({
  title,
  submitLabel,
  onCancel,
  onSubmit,
  showCancel,
  cancelColor,
  submitColor,
  disabled = false,
  showSearch = false,
  onSearch,
  isLoading = false,
}) => {
  const { t } = useTranslation('shared');
  return (
    <Box flexDirection='row' justifyContent='space-between'>
      {showCancel && (
        <TouchableOpacity
          onPress={onCancel}
          disabled={isLoading}
          accessibilityLabel='Cancel'>
          <Text
            variant='buttonLabel'
            color={isLoading ? 'grey04' : cancelColor || 'grey04'}>
            {t('cancel')}
          </Text>
        </TouchableOpacity>
      )}
      <Box
        style={{ position: `absolute`, left: 0, right: 0 }}
        alignItems='center'
        pointerEvents='none'>
        <Text variant='labelEmphasized'>{title}</Text>
      </Box>
      {onSubmit && (
        <TouchableOpacity
          onPress={onSubmit}
          disabled={disabled || isLoading}
          accessibilityLabel={submitLabel}>
          {isLoading ? (
            <Spinner
              size={16}
              strokeWidth={2}
              color={theme.colors[submitColor || 'greenSecondary']}
            />
          ) : (
            <Text variant='buttonLabel' color={submitColor || 'greenSecondary'}>
              {submitLabel}
            </Text>
          )}
        </TouchableOpacity>
      )}

      {showSearch && (
        <Icon
          name='Search'
          variant='m'
          color='textPrimary'
          onPress={() => onSearch?.()}
          accessibilityLabel='Search'
        />
      )}
    </Box>
  );
};
